import type { ApiEndpoint } from "@/shared/api-endpoint";
import { isNumberArray, isObject, isStringArray } from "@/shared/utils/typeGuards";

export const kLoopToKitRoute = "/loop-to-kit";

// Catalog one-shots returned per slot; clamped server-side by the Modal search (MAX_K).
export const kMinLoopToKitResults = 1;
export const kMaxLoopToKitResults = 100;
export const kDefaultLoopToKitResults = 10;

// The timbre model emits at most 12 active slots, so `slots`/`kit` never exceed this.
export const kMaxLoopToKitSlots = 12;

export interface LoopToKitRequestBody {
    fileKey: string; // S3 key of the source drum loop
    bpm?: number; // the loop's tempo; estimated server-side when omitted (valid range 80-159)
    k?: number; // catalog one-shots per slot (default kDefaultLoopToKitResults)
    seed?: number; // sampling seed for both diffusion stages; -1 = random
}

// One predicted drum voice heard in the loop. `lane` is the drum-class index
// (0=kick … 8=other), `label` its name; a lane can own several slots (e.g. two snares).
export interface LoopToKitSlot {
    slot: number; // position 0-11 in the model's 12-slot output
    lane: number; // drum-class index 0-8
    label: string; // lane name, e.g. "kick", "hihat_closed"
    embedding: number[]; // 512-d unit-norm CL-SiSo timbre vector
}

// Retrieval results for one slot; positionally 1:1 with `slots` (kit[i] is slots[i]'s pad).
export interface LoopToKitPad {
    slot: number;
    lane: number;
    label: string;
    uuids: string[]; // Splice catalog sample uuids, best match first
    fileHashes: string[]; // positionally aligned with uuids
    scores: number[]; // cosine similarities, descending
}

// A transcribed drum hit. `refIndex` is the LANE index (pitch = 36 + lane), not a pad
// index — when two slots share a lane, their hits arrive merged in that lane.
export interface LoopToKitNote {
    pitch: number; // MIDI note number (36 + lane)
    pitchName: string; // e.g. "C2"
    refIndex: number; // lane index 0-8
    startSeconds: number;
    endSeconds: number;
    durationSeconds: number;
    velocity: number; // constant 100 — the trigger grid is binary
}

export interface LoopToKitTranscription {
    midiDownloadUrl: string; // presigned URL for the transcribed .mid file
    notes: LoopToKitNote[]; // flat list of hits, sorted by start time then pitch
    bpm: number; // tempo stamped on the output MIDI (rounded to int)
    durationSeconds: number; // loop duration (quantized to whole latent frames)
    noteCount: number; // convenience: notes.length
}

export interface LoopToKitSuccessResponse {
    ok: true;
    bpm: number;
    bpmEstimated: boolean; // true when the server's bpm classifier estimated it
    slots: LoopToKitSlot[]; // active slots only, in model-return order
    kit: LoopToKitPad[]; // positionally 1:1 with slots
    transcription: LoopToKitTranscription;
}

export interface LoopToKitErrorResponse {
    ok: false;
    error: string;
}

export type LoopToKitResponse = LoopToKitSuccessResponse | LoopToKitErrorResponse;

export const isLoopToKitRequestBody = (value: unknown): value is LoopToKitRequestBody =>
    isObject(value) &&
    typeof value.fileKey === "string" &&
    (value.bpm === undefined || typeof value.bpm === "number") &&
    (value.k === undefined || typeof value.k === "number") &&
    (value.seed === undefined || typeof value.seed === "number");

const isLoopToKitSlot = (value: unknown): value is LoopToKitSlot =>
    isObject(value) &&
    typeof value.slot === "number" &&
    typeof value.lane === "number" &&
    typeof value.label === "string" &&
    isNumberArray(value.embedding);

const isLoopToKitPad = (value: unknown): value is LoopToKitPad =>
    isObject(value) &&
    typeof value.slot === "number" &&
    typeof value.lane === "number" &&
    typeof value.label === "string" &&
    isStringArray(value.uuids) &&
    isStringArray(value.fileHashes) &&
    isNumberArray(value.scores);

const isLoopToKitNote = (value: unknown): value is LoopToKitNote =>
    isObject(value) &&
    typeof value.pitch === "number" &&
    typeof value.pitchName === "string" &&
    typeof value.refIndex === "number" &&
    typeof value.startSeconds === "number" &&
    typeof value.endSeconds === "number" &&
    typeof value.durationSeconds === "number" &&
    typeof value.velocity === "number";

const isLoopToKitTranscription = (value: unknown): value is LoopToKitTranscription =>
    isObject(value) &&
    typeof value.midiDownloadUrl === "string" &&
    Array.isArray(value.notes) &&
    value.notes.every(isLoopToKitNote) &&
    typeof value.bpm === "number" &&
    typeof value.durationSeconds === "number" &&
    typeof value.noteCount === "number";

export const isLoopToKitSuccessResponse = (value: unknown): value is LoopToKitSuccessResponse =>
    isObject(value) &&
    value.ok === true &&
    typeof value.bpm === "number" &&
    typeof value.bpmEstimated === "boolean" &&
    Array.isArray(value.slots) &&
    value.slots.every(isLoopToKitSlot) &&
    Array.isArray(value.kit) &&
    value.kit.every(isLoopToKitPad) &&
    isLoopToKitTranscription(value.transcription);

export const isLoopToKitErrorResponse = (value: unknown): value is LoopToKitErrorResponse =>
    isObject(value) && value.ok === false && typeof value.error === "string";

export const kLoopToKitEndpoint: ApiEndpoint<LoopToKitRequestBody, LoopToKitSuccessResponse> = {
    route: kLoopToKitRoute,
    label: "Loop to Kit",
    source: "Modal",
    category: "Semantic Intent",
    description:
        "Deconstructs a drum loop in one call: per-slot drum labels + timbre embeddings, the top-k nearest Splice one-shots per slot, and a transcription of the loop's pattern as drum MIDI.",
    isRequestBody: isLoopToKitRequestBody,
    isSuccessResponse: isLoopToKitSuccessResponse,
};
